import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Lock, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { confirmPasswordReset } from '../api/api';

export const AdminPasswordReset = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [termine, setTermine] = useState(false);

  const assezFort = motDePasse.length >= 10 && /[A-Za-z]/.test(motDePasse) && /\d/.test(motDePasse);
  const correspondent = motDePasse === confirmation && confirmation.length > 0;

  const soumettre = async (e) => {
    e.preventDefault();
    if (!assezFort || !correspondent) return;

    setEnvoiEnCours(true);
    try {
      await confirmPasswordReset(token, motDePasse);
      setTermine(true);
      toast.success('Mot de passe réinitialisé.');
      setTimeout(() => navigate('/admin/login'), 2500);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Le lien est invalide ou a expiré. Demandez-en un nouveau."
      );
    } finally {
      setEnvoiEnCours(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <h1 className="text-xl font-bold text-gray-900 mb-2">Lien incomplet</h1>
            <p className="text-gray-600 mb-6">
              Ce lien de réinitialisation est incomplet. Utilisez celui reçu par email.
            </p>
            <Link to="/admin/login" className="text-blue-600 underline">
              Retour à la connexion
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center">
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
            <Lock className="w-6 h-6 text-blue-600" aria-hidden="true" />
          </div>
          <CardTitle className="text-2xl">Nouveau mot de passe</CardTitle>
        </CardHeader>

        <CardContent>
          {termine ? (
            <div className="text-center py-6">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" aria-hidden="true" />
              <p className="text-gray-700">
                Mot de passe modifié. Redirection vers la connexion…
              </p>
            </div>
          ) : (
            <form onSubmit={soumettre} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="mdp">Nouveau mot de passe</Label>
                <Input
                  id="mdp"
                  type="password"
                  autoComplete="new-password"
                  value={motDePasse}
                  onChange={(e) => setMotDePasse(e.target.value)}
                  className="h-12"
                  required
                />
                <p className={`text-xs ${assezFort ? 'text-green-600' : 'text-gray-500'}`}>
                  Au moins 10 caractères, avec des lettres et des chiffres.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mdp2">Confirmez le mot de passe</Label>
                <Input
                  id="mdp2"
                  type="password"
                  autoComplete="new-password"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  className="h-12"
                  required
                />
                {confirmation && !correspondent && (
                  <p className="text-xs text-red-600">Les mots de passe ne correspondent pas.</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={!assezFort || !correspondent || envoiEnCours}
                className="w-full h-12"
              >
                {envoiEnCours ? 'Enregistrement…' : 'Définir le mot de passe'}
              </Button>

              <p className="text-center text-sm">
                <Link to="/admin/login" className="text-gray-500 hover:text-blue-600">
                  Retour à la connexion
                </Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPasswordReset;
